import { HTMLAttributes, ReactNode, useMemo } from 'react';

import { Spinner } from '@material-tailwind/react';
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getGroupedRowModel,
  useReactTable,
} from '@tanstack/react-table';

import {
  TableBody,
  TableCaption,
  TableCaptionProps,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableWrapper,
} from '@components/table/parts';

import { cn } from '@utils/tailwind/cn';
import { CSSVariables } from '@utils/tailwind/cssVariables';
import { getArrayLayer, recursionColumns } from '@utils/tanstack-table';

export interface BasicTableProps
  extends HTMLAttributes<HTMLDivElement>,
    Pick<TableCaptionProps, 'captionAlign' | 'captionPosition'> {
  caption?: string | ReactNode;
  isLoading?: boolean;
  columns: ColumnDef<{ [name: string]: any }>[];
  data: { [name: string]: any }[];
}

export const BasicTable = ({
  className,
  caption,
  isLoading = false,
  captionAlign,
  captionPosition,
  columns: initColums,
  data,
  ...props
}: BasicTableProps) => {
  const [columns] = useMemo<
    [ColumnDef<{ [name: string]: any }>[], number]
  >(() => {
    const layer = getArrayLayer(initColums);
    return [recursionColumns(initColums, 0, layer), layer];
  }, [initColums]);

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getGroupedRowModel: getGroupedRowModel(),
  });

  return (
    <section
      style={CSSVariables({
        head: {
          'bg-color': '#ededed',
          'border-color': '#d1d1d1',
          'text-color': '#000000',
        },
        body: {
          'border-color': '#adadad',
        },
      })}
      className={cn('flex w-full flex-col', className)}
      {...props}
    >
      <TableWrapper>
        {caption && (
          <TableCaption
            captionAlign={captionAlign}
            captionPosition={captionPosition}
          >
            {caption}
          </TableCaption>
        )}
        <TableHeader className='border-collapse border border-[var(--head-border-color)] bg-[var(--head-bg-color)] [&_tr]:text-sm [&_tr]:text-[var(--head-text-color)]'>
          {table
            .getHeaderGroups()
            .reduce((acc, cur) => {
              return acc.concat(
                cur.headers.filter((v) => !v.isPlaceholder) as any,
              );
            }, [])
            .reduce((acc, cur) => {
              // @ts-ignore
              const __depth = cur.column.columnDef.__depth;

              if (!Array.isArray(acc[__depth])) acc[__depth] = [cur];
              else acc[__depth].push(cur);
              return acc;
            }, [] as any[])
            .map((items) => items.sort((a: any, b: any) => a.index - b.index))
            .map((headerGroup, i) => (
              <TableRow key={`table-row-${i}`}>
                {headerGroup.map((header: any) => {
                  // @ts-ignore
                  const __rowSpan = header?.column?.columnDef?.__rowSpan;
                  // @ts-ignore
                  const __colSpan = header?.column?.columnDef?.__colSpan;
                  // eslint-disable-next-line no-unsafe-optional-chaining
                  const { minSize, maxSize } = header?.column?.columnDef;

                  return (
                    <TableHead
                      key={header.id}
                      colSpan={__colSpan}
                      rowSpan={__rowSpan}
                      className={cn(
                        'whitespace-pre-wrap border border-[var(--head-border-color)] py-3 text-center',
                        `min-w-[${minSize}px] w-[${minSize}px] max-w-[${maxSize}px]`,
                      )}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
        </TableHeader>
        <TableBody
          className={cn(
            'h-fit border-collapse border border-[var(--body-border-color)]',
          )}
        >
          {isLoading && table.getRowModel().rows.length == 0 && (
            <TableRow>
              <TableCell colSpan={100} className='py-28'>
                <Spinner className='mx-auto h-10 w-10' />
              </TableCell>
            </TableRow>
          )}
          {(!isLoading || table.getRowModel().rows.length > 0) &&
            table.getRowModel().rows.map((row) => {
              return (
                <TableRow key={row.id} className='w-full border'>
                  {row.getVisibleCells().map((cell) => {
                    // eslint-disable-next-line no-unsafe-optional-chaining
                    const { minSize, maxSize } = cell?.column?.columnDef;

                    return (
                      <TableCell
                        key={cell.id}
                        className={cn(
                          'break-all border border-[var(--body-border-color)] p-0 py-3 text-center text-[13px] font-medium',
                        )}
                      >
                        <span
                          className={cn(
                            'flex justify-center whitespace-pre-line break-all text-center font-medium',
                            `min-w-[${minSize}px] w-[${minSize}px] max-w-[${maxSize}px]`,
                          )}
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext(),
                          )}
                        </span>
                      </TableCell>
                    );
                  })}
                </TableRow>
              );
            })}
        </TableBody>
      </TableWrapper>
    </section>
  );
};
